import React, { useEffect, useRef, useState } from "react";
import { deletePatient } from "../data/ccProgram";

/* Inline SVG — no external icon-library dependency. */
function MoreVertical({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function Trash2({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

const STATUS_LABEL = {
  active: "Active",
  on_boarding: "On-boarding",
  needs_attention: "Needs Attention",
  completed: "Completed",
};

const STATUS_COLORS = {
  active: { bg: "#e7f9ef", color: "#1fa971" },
  on_boarding: { bg: "#eef1fe", color: "#5b6bda" },
  needs_attention: { bg: "#fff1e6", color: "#e8822f" },
  completed: { bg: "#efecfd", color: "#6C5CE7" },
};

function normalizeStatusKey(status) {
  return (status || "").toLowerCase().replace(/[\s-]/g, "_");
}

function formatPhase(phase) {
  if (phase === null || phase === undefined || phase === "") return null;
  // If it's already text like "Phase 2", leave it. If it's a bare number, prefix it.
  const isPlainNumber = typeof phase === "number" || /^\d+$/.test(String(phase).trim());
  return isPlainNumber ? `Phase ${phase}` : String(phase);
}

/* ── Everything below is plain inline style objects — nothing here
   depends on an external CSS file loading correctly. Column widths
   (GRID_STYLE) are the single source of truth used by BOTH the header
   and every row, so they can never drift out of alignment. ── */

const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "2.2fr 2fr 1fr 1fr 0.4fr",
  alignItems: "center",
  gap: "12px",
  width: "100%",
  boxSizing: "border-box",
};

const pillStyle = (bg, color) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 11px",
  borderRadius: "999px",
  fontSize: "11.5px",
  fontWeight: 700,
  whiteSpace: "nowrap",
  background: bg,
  color: color,
  fontFamily: "'Satoshi','DM Sans','Segoe UI',sans-serif",
});

/* Condition pill gets its own style: wraps onto a second line instead of
   overflowing or forcing the row wider, since some conditions are
   multi-word ("Hypertension, PCOS / PCOD"). */
const conditionPillStyle = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "14px",
  fontSize: "11.5px",
  fontWeight: 700,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.35,
  textAlign: "center",
  maxWidth: "100%",
  background: "#e7f9ef",
  color: "#1fa971",
  fontFamily: "'Satoshi','DM Sans','Segoe UI',sans-serif",
};

/**
 * PatientTable
 * Props:
 *  - patients        (array)
 *  - loading         (bool)
 *  - onRowClick      (fn(patient))
 *  - onPatientDeleted (fn(patient))  optional — called after a successful delete
 *                                     so the parent can refetch / filter its list
 */
export default function PatientTable({ patients = [], loading = false, onRowClick, onPatientDeleted }) {
  const [openMenuId, setOpenMenuId] = useState(null);   // patient_id whose 3-dot menu is open
  const [confirmTarget, setConfirmTarget] = useState(null); // patient object pending delete confirmation
  const [deletingId, setDeletingId] = useState(null);   // patient_id currently being deleted
  const [deleteError, setDeleteError] = useState(null);
  const menuRef = useRef(null);

  // Close the open dropdown on outside click.
  useEffect(() => {
    if (!openMenuId) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  function handleMenuToggle(e, patientId) {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === patientId ? null : patientId));
  }

  function handleDeleteClick(e, patient) {
    e.stopPropagation();
    setOpenMenuId(null);
    setDeleteError(null);
    setConfirmTarget(patient);
  }

  function closeConfirm() {
    if (deletingId) return; // don't allow closing mid-request
    setConfirmTarget(null);
    setDeleteError(null);
  }

  async function handleConfirmDelete() {
    const patient = confirmTarget;
    if (!patient) return;

    const patientId = patient.patient_id;
    const programId = patient.program_id ?? patient.programId; // optional — API allows deleting without it

    if (!patientId) {
      setDeleteError("Missing patient_id — can't delete.");
      return;
    }

    setDeletingId(patientId);
    setDeleteError(null);
    try {
      await deletePatient({ patientId, programId });
      setConfirmTarget(null);
      setDeletingId(null);
      onPatientDeleted && onPatientDeleted(patient);
    } catch (err) {
      setDeletingId(null);
      setDeleteError(err.message || "Failed to delete patient.");
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", fontFamily: "'Satoshi','DM Sans','Segoe UI',sans-serif" }}>
      {/* HEADER */}
      <div style={{ ...GRID_STYLE, padding: "14px 24px 10px" }}>
        {["Patients", "Condition", "Current Phase", "Status"].map((h) => (
          <span
            key={h}
            style={{
              fontSize: "11.5px",
              fontWeight: 700,
              color: "#97a1b5",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {h}
          </span>
        ))}
        <span
          style={{
            fontSize: "11.5px",
            fontWeight: 700,
            color: "#97a1b5",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
            textAlign: "right",
          }}
        >
          Action
        </span>
      </div>

      {/* BODY */}
      <div
        style={{
          maxHeight: "340px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "0 24px 16px",
        }}
      >
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "14px", padding: "60px 24px", color: "#94a3b8", fontSize: "14px" }}>
            Loading patients…
          </div>
        )}

        {!loading && patients.length === 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px", color: "#94a3b8", fontSize: "14px" }}>
            No patients found.
          </div>
        )}

        {!loading && patients.map((p) => {
          const statusKey = normalizeStatusKey(p?.status);
          const statusColor = STATUS_COLORS[statusKey] || { bg: "#f1f5f9", color: "#55607a" };
          const isMenuOpen = openMenuId === p.patient_id;
          const isRowDeleting = deletingId === p.patient_id;

          return (
            <div
              key={p.patient_id}
              style={{
                ...GRID_STYLE,
                padding: "16px 20px",
                background: "#fafbfc",
                borderRadius: "12px",
                border: "1px solid #f2f4f8",
                cursor: "pointer",
                opacity: isRowDeleting ? 0.5 : 1,
                pointerEvents: isRowDeleting ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
              onClick={() => onRowClick && onRowClick(p)}
            >
              {/* Patient name + subtext */}
              <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <span style={{ fontSize: "15px", fontWeight: 500, color: "#1e2433" }}>
                  {p?.user_profile?.name || "—"}
                </span>
                <span style={{ fontSize: "11.5px", color: "#97a1b5" }}>
                  Age:{p?.user_profile?.age ?? "—"} {p?.user_profile?.phone_no || ""}
                </span>
              </div>

              {/* Condition */}
              <div style={{ minWidth: 0 }}>
                {p?.condition && (
                  <span style={conditionPillStyle}>{p.condition}</span>
                )}
              </div>

              {/* Current phase */}
              <div>
                {formatPhase(p?.current_phase) && (
                  <span style={{ ...pillStyle("#eef1fe", "#5b6bda"), minWidth: "56px", justifyContent: "center" }}>
                    {formatPhase(p.current_phase)}
                  </span>
                )}
              </div>

              {/* Status */}
              <div>
                <span style={pillStyle(statusColor.bg, statusColor.color)}>
                  {STATUS_LABEL[statusKey] || p?.status || "—"}
                </span>
              </div>

              {/* Action */}
              <div style={{ display: "flex", justifyContent: "flex-end", position: "relative" }}>
                <button
                  onClick={(e) => handleMenuToggle(e, p.patient_id)}
                  aria-label="Row actions"
                  style={{
                    border: "none",
                    background: "none",
                    color: "#97a1b5",
                    cursor: "pointer",
                    padding: "4px",
                    borderRadius: "6px",
                    display: "flex",
                  }}
                >
                  <MoreVertical size={16} />
                </button>

                {isMenuOpen && (
                  <div
                    ref={menuRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "28px",
                      right: 0,
                      background: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0 8px 24px rgba(20,25,40,0.14)",
                      border: "1px solid #f0f2f6",
                      minWidth: "140px",
                      zIndex: 20,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={(e) => handleDeleteClick(e, p)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        border: "none",
                        background: "none",
                        padding: "10px 14px",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#e5484d",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* CONFIRM DELETE MODAL */}
      {confirmTarget && (
        <div
          onClick={closeConfirm}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,25,40,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "24px",
              width: "360px",
              maxWidth: "90vw",
              boxShadow: "0 20px 60px rgba(20,25,40,0.25)",
              fontFamily: "'Satoshi','DM Sans','Segoe UI',sans-serif",
            }}
          >
            <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e2433", marginBottom: "8px" }}>
              Remove patient?
            </div>
            <div style={{ fontSize: "13.5px", color: "#616b80", lineHeight: 1.5, marginBottom: "18px" }}>
              This will remove{" "}
              <strong>{confirmTarget?.user_profile?.name || "this patient"}</strong>{" "}
              from the program. This action can't be undone.
            </div>

            {deleteError && (
              <div
                style={{
                  fontSize: "12.5px",
                  color: "#e5484d",
                  background: "#fff1f0",
                  border: "1px solid #ffd6d6",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  marginBottom: "14px",
                }}
              >
                {deleteError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={closeConfirm}
                disabled={!!deletingId}
                style={{
                  border: "1px solid #e5e8ef",
                  background: "#fff",
                  color: "#55607a",
                  fontWeight: 600,
                  fontSize: "13px",
                  padding: "9px 16px",
                  borderRadius: "9px",
                  cursor: deletingId ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={!!deletingId}
                style={{
                  border: "none",
                  background: "#e5484d",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "13px",
                  padding: "9px 16px",
                  borderRadius: "9px",
                  cursor: deletingId ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  opacity: deletingId ? 0.7 : 1,
                }}
              >
                {deletingId ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}